import {
  type UseMutationResult,
  type UseQueryResult,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import { adminMutationKeys, adminQueryKeys } from '@/hooks/admin/query-keys';
import { retryControlApi } from '@/hooks/admin/retry';
import {
  tenantAdminService,
  tenantUsersService,
} from '@/services/control/tenant-users-service';
import type {
  ShardMigrationJob,
  SyncStatusSummary,
  TenantDeleteResult,
  TenantUser,
  TenantUserInput,
} from '@/types/control/tenant-user';

/**
 * Sync status for every tenant.
 *
 * Polled, because a running sync finishes without anyone acting. The interval
 * alone keeps it off a hidden tab — react-query gates the timer on the focus
 * manager, so `refetchIntervalInBackground` is deliberately left unset.
 *
 * Note this endpoint answers a **500** when the sync admin is unwired, not a
 * 503, so `retryControlApi` will retry it twice on each tick in that state.
 * That is accepted rather than special-cased: the alternative is matching on an
 * error string that is not part of any contract.
 */
export function useTenantSyncStatus(): UseQueryResult<
  SyncStatusSummary,
  Error
> {
  return useQuery<SyncStatusSummary, Error>({
    queryKey: adminQueryKeys.tenantSyncStatus(),
    queryFn: ({ signal }) => tenantAdminService.syncStatus(signal),
    refetchInterval: 10_000,
    retry: retryControlApi,
  });
}

export function useTenantUsers(
  tenantId: string | null
): UseQueryResult<TenantUser[], Error> {
  return useQuery<TenantUser[], Error>({
    queryKey: adminQueryKeys.tenantUsers(tenantId ?? undefined),
    queryFn: ({ signal }) =>
      tenantUsersService.list(tenantId as string, signal),
    enabled: Boolean(tenantId),
    retry: retryControlApi,
  });
}

export function useAddTenantUser(
  tenantId: string
): UseMutationResult<void, Error, TenantUserInput> {
  const queryClient = useQueryClient();

  return useMutation<void, Error, TenantUserInput>({
    mutationKey: adminMutationKeys.addTenantUser(tenantId),
    mutationFn: (input) => tenantUsersService.add(tenantId, input),
    // The 201 body is not trusted — it carries a differently-typed created_at
    // and no roles — so the list is refetched rather than patched.
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: adminQueryKeys.tenantUsers(tenantId),
      }),
  });
}

export function useTriggerTenantSync(): UseMutationResult<void, Error, string> {
  const queryClient = useQueryClient();

  return useMutation<void, Error, string>({
    mutationKey: adminMutationKeys.triggerTenantSync(),
    mutationFn: (tenantId) => tenantAdminService.triggerSync(tenantId),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: adminQueryKeys.tenantSyncStatus(),
      }),
  });
}

/**
 * Enqueues a shard migration. Nothing local is invalidated: the 202 means the
 * job was accepted, and the migration's effects land on the worker's schedule
 * rather than by the time this resolves.
 */
export function useMigrateTenantShards(): UseMutationResult<
  ShardMigrationJob,
  Error,
  string
> {
  return useMutation<ShardMigrationJob, Error, string>({
    mutationKey: adminMutationKeys.migrateTenantShards(),
    mutationFn: (tenantId) => tenantAdminService.migrateShards(tenantId),
  });
}

/**
 * Deleting resolves even for a partial failure — that is not an error, it is a
 * result the caller has to show. The mutation succeeds and hands back what was
 * left behind.
 */
export function useDeleteTenant(): UseMutationResult<
  TenantDeleteResult,
  Error,
  string
> {
  const queryClient = useQueryClient();

  return useMutation<TenantDeleteResult, Error, string>({
    mutationKey: adminMutationKeys.deleteTenant(),
    mutationFn: (tenantId) => tenantAdminService.remove(tenantId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.tenants() });
      queryClient.invalidateQueries({
        queryKey: adminQueryKeys.tenantSyncStatus(),
      });
    },
  });
}
